/** Org-scoped setup wizard (name, bio, logo, posting prefs) — a single onboarding-style journey, separate from account onboarding UI. */

/** First step when creating a brand-new organisation; not part of account onboarding. */
export const EMPLOYER_ORG_CREATE_STEP_ID = 'org_name' as const

export const EMPLOYER_ORG_SETUP_STEP_IDS = [
  'company_bio',
  'company_logo',
  'posting_preferences',
] as const

export type EmployerOrgSetupStepId = (typeof EMPLOYER_ORG_SETUP_STEP_IDS)[number]

export interface EmployerOrgSetupStep {
  id: EmployerOrgSetupStepId | typeof EMPLOYER_ORG_CREATE_STEP_ID
  title: string
  description: string
  component: string
}

const ORG_NAME_STEP: EmployerOrgSetupStep = {
  id: EMPLOYER_ORG_CREATE_STEP_ID,
  title: 'Organisation name',
  description: 'What is your company or team called?',
  component: 'employer-org-name-step',
}

/** Steps for continuing setup on an org that already exists (resume, or edit-in-place). */
export const EMPLOYER_ORG_SETUP_STEPS: EmployerOrgSetupStep[] = [
  {
    id: 'company_bio',
    title: 'About your company',
    description: 'Add a short bio candidates will see on your profile.',
    component: 'employer-company-bio-step',
  },
  {
    id: 'company_logo',
    title: 'Company logo',
    description: 'Add your logo so candidates recognise your brand.',
    component: 'employer-company-logo-step',
  },
  {
    id: 'posting_preferences',
    title: 'Job posting preferences',
    description: 'Set defaults for new job postings.',
    component: 'employer-posting-preferences-step',
  },
]

/** Full journey for a brand-new organisation: name first, then the resume steps. One continuous flow, one page. */
export const EMPLOYER_ORG_CREATE_STEPS: EmployerOrgSetupStep[] = [
  ORG_NAME_STEP,
  ...EMPLOYER_ORG_SETUP_STEPS,
]

/** Account onboarding steps that run in the org setup wizard instead of /onboarding. */
export const EMPLOYER_ORG_SETUP_ONBOARDING_STEP_IDS = new Set<string>(
  EMPLOYER_ORG_SETUP_STEP_IDS,
)

/** Entry point for "add organisation" — a brand-new, unsaved org (slug assigned after the name step). */
export const EMPLOYER_ORGANISATION_NEW_SETUP_PATH = '/employer/organisation/new/setup'

export function employerOrgSetupPath(slug: string, stepId?: string): string {
  const trimmed = slug.trim()
  const base = `/employer/organisation/${encodeURIComponent(trimmed)}/setup`
  if (!stepId?.trim()) return base
  return `${base}?step=${encodeURIComponent(stepId.trim())}`
}

export function employerOrgCreateSetupPath(stepId?: string): string {
  if (!stepId?.trim()) return EMPLOYER_ORGANISATION_NEW_SETUP_PATH
  return `${EMPLOYER_ORGANISATION_NEW_SETUP_PATH}?step=${encodeURIComponent(stepId.trim())}`
}

export function employerOrgSetupStepIndex(
  steps: EmployerOrgSetupStep[],
  stepId: string,
): number {
  return steps.findIndex((step) => step.id === stepId)
}

export function clampEmployerOrgSetupStepIndex(
  steps: EmployerOrgSetupStep[],
  requestedIndex: number,
): number {
  if (steps.length === 0) return 0
  if (requestedIndex < 0) return 0
  return Math.min(requestedIndex, steps.length - 1)
}
